import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Upload, Camera, Download, Share2, ArrowLeft,
  RefreshCw, Heart, ImageIcon, Lock, Crown, AlertCircle, X, ArrowRight, Palette,
  MoreVertical, Archive, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AuthenticatedHeader } from '@/components/layout/AuthenticatedHeader';
import { useAffirmationImages, type PlanTier } from '@/hooks/useAffirmationImages';
import { AffirmationStyleControls, DEFAULT_STYLE_SETTINGS, type StyleSettings } from '@/components/affirmation/AffirmationStyleControls';
import { toast } from 'sonner';

type Screen = 'hub' | 'upload' | 'style' | 'generating' | 'result' | 'limit';

function getPlanLabel(tier: PlanTier): string {
  switch (tier) {
    case 'free': return 'Free';
    case 'starter': return 'Starter';
    case 'connection': return 'Connection';
    case 'legacy': return 'Legacy';
  }
}

function getUsageText(tier: PlanTier, used: number, cap: number): string {
  if (cap <= 0) return 'Plan limit unavailable';
  if (tier === 'free') return `${used} of ${cap} used`;
  return `${used} of ${cap} used this month`;
}

export default function AffirmationImageGenerator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const remixId = searchParams.get('remix');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    config, usage, gallery, archivedCount, isLoading, isGenerating,
    isPaid, planTier, canGenerate, getCapForPlan, generateImage,
    archiveImage, restoreArchivedImages, deleteImage,
  } = useAffirmationImages();

  const [screen, setScreen] = useState<Screen>('hub');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<{ imageUrl: string; affirmationText: string } | null>(null);
  const [progress, setProgress] = useState(0);
  const [styleSettings, setStyleSettings] = useState<StyleSettings>({ ...DEFAULT_STYLE_SETTINGS });
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; storagePath: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getShareUrl = useCallback(() => {
    if (gallery.length > 0) {
      return `${window.location.origin}/affirmation/${gallery[0].id}`;
    }
    return result?.imageUrl ?? '';
  }, [gallery, result]);

  useEffect(() => {
    if (remixId && screen === 'hub' && !isLoading) {
      setScreen('upload');
    }
  }, [remixId, isLoading]);

  const cap = getCapForPlan();
  const used = Math.min(cap, usage?.generation_count ?? 0);
  const remaining = Math.max(0, cap - used);
  const displayedGallery = showAllGallery ? gallery : gallery.slice(0, 6);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset input value so the same file can be re-selected
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setSelectedPhoto(ev.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!selectedPhoto) return;
    if (!canGenerate()) {
      setScreen('limit');
      return;
    }

    setScreen('generating');
    setProgress(0);

    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + Math.random() * 8, 90));
    }, 500);

    try {
      const res = await generateImage(selectedPhoto, styleSettings as unknown as Record<string, unknown>);
      clearInterval(interval);
      setProgress(100);
      setResult(res);
      setTimeout(() => setScreen('result'), 300);
    } catch (err: unknown) {
      clearInterval(interval);
      const message = err instanceof Error ? err.message : '';
      if (message === 'limit_reached') {
        setScreen('limit');
      } else {
        setScreen('style');
        toast.error(message || 'Generation failed. Please try again.');
      }
    }
  }, [selectedPhoto, canGenerate, generateImage, styleSettings]);

  const handleShare = useCallback(async () => {
    if (!result?.imageUrl) return;
    const url = getShareUrl();
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Personalized Affirmation | Say It Now',
          text: 'Check out my personalized affirmation!',
          url,
        });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  }, [result, getShareUrl]);

  const handleDownload = useCallback(async () => {
    if (!result?.imageUrl) return;
    try {
      const response = await fetch(result.imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `affirmation-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Image downloaded!');
    } catch {
      toast.error('Download failed');
    }
  }, [result]);

  const handleArchive = useCallback((imageId: string) => {
    archiveImage(imageId);
    toast.success('Affirmation archived from this list');
  }, [archiveImage]);

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;

    setIsDeleting(true);
    try {
      await deleteImage(pendingDelete.id, pendingDelete.storagePath);
      toast.success('Affirmation deleted');
      setPendingDelete(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteImage, pendingDelete]);

  const resetFlow = useCallback(() => {
    setSelectedPhoto(null);
    setSelectedFile(null);
    setResult(null);
    setProgress(0);
    setScreen('hub');
  }, []);

  const startNew = useCallback(() => {
    setSelectedPhoto(null);
    setSelectedFile(null);
    setResult(null);
    setProgress(0);
    setScreen('upload');
  }, []);

  const tryAnotherStyle = useCallback(() => {
    setResult(null);
    setProgress(0);
    setScreen('style');
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader />
        <div className="flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!config?.feature_enabled) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader />
        <div className="max-w-md mx-auto px-4 pt-24 text-center">
          <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Coming Soon</h2>
          <p className="text-muted-foreground">This feature is not yet available.</p>
          <Button variant="outline" className="mt-6" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader />
      <div className="max-w-lg mx-auto px-4 pt-6 pb-24">
        <AnimatePresence mode="wait">
          {/* ── SCREEN 1: HUB ── */}
          {screen === 'hub' && (
            <motion.div
              key="hub"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Dashboard
              </button>

              <Card className="overflow-hidden border-primary/20">
                <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2.5 rounded-xl bg-primary/10">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h1 className="text-xl font-bold text-foreground leading-tight">
                        Create a personalized affirmation with your photo
                      </h1>
                      <p className="text-sm text-muted-foreground mt-1.5">
                        Before you pour into others, pour into yourself.
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-1 italic">
                        What you say to yourself matters just as much.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-5 flex-wrap">
                    <Badge variant="outline" className="gap-1 text-xs">
                      {isPaid ? <Crown className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                      {getPlanLabel(planTier)} Plan
                    </Badge>
                    <Badge
                      variant={remaining > 0 ? 'secondary' : 'destructive'}
                      className="gap-1 text-xs"
                    >
                      {getUsageText(planTier, used, cap)}
                    </Badge>
                  </div>

                  <Button
                    size="lg"
                    className="w-full gap-2"
                    onClick={() => {
                      if (!canGenerate()) {
                        setScreen('limit');
                      } else {
                        setScreen('upload');
                      }
                    }}
                  >
                    <Upload className="h-4 w-4" />
                    Upload Photo
                  </Button>
                </div>
              </Card>

              {(gallery.length > 0 || archivedCount > 0) && (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">My Affirmations</h3>
                      <p className="text-xs text-muted-foreground">
                        {gallery.length} visible{archivedCount > 0 ? ` · ${archivedCount} archived` : ''}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {archivedCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={restoreArchivedImages}
                        >
                          Show Archived
                        </Button>
                      )}
                      {gallery.length > 6 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => setShowAllGallery((prev) => !prev)}
                        >
                          {showAllGallery ? 'Show Less' : `Show All (${gallery.length})`}
                        </Button>
                      )}
                    </div>
                  </div>

                  {gallery.length === 0 ? (
                    <Card>
                      <CardContent className="p-4 text-sm text-muted-foreground">
                        Archived affirmations are hidden from this view.
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {displayedGallery.map((img) => (
                        <Card key={img.id} className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => {
                            setResult({ imageUrl: img.image_url, affirmationText: img.affirmation_text });
                            setScreen('result');
                          }}
                        >
                          <div className="relative">
                            <div className="absolute right-2 top-2 z-10">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="secondary"
                                    size="icon"
                                    className="h-8 w-8 rounded-full bg-background/85 backdrop-blur"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleArchive(img.id);
                                    }}
                                  >
                                    <Archive className="mr-2 h-4 w-4" />
                                    Archive from list
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setPendingDelete({ id: img.id, storagePath: img.original_photo_path });
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete permanently
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>

                            <img
                              src={img.image_url}
                              alt={img.affirmation_text}
                              className="w-full aspect-[4/5] object-cover"
                              loading="lazy"
                            />
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* ── SCREEN 2: UPLOAD ── */}
          {screen === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <button
                onClick={resetFlow}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <div className="text-center space-y-2">
                {remixId && (
                  <Badge variant="secondary" className="gap-1 mb-1">
                    <RefreshCw className="h-3 w-3" />
                    Remixing style — Make it yours
                  </Badge>
                )}
                <h2 className="text-xl font-bold text-foreground">Upload your photo</h2>
                <p className="text-sm text-muted-foreground">
                  Choose a clear photo of yourself to create a personalized affirmation image.
                </p>
              </div>

              <Card
                className="border-2 border-dashed border-primary/30 hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <CardContent className="p-8">
                  {selectedPhoto ? (
                    <div className="relative">
                      <img
                        src={selectedPhoto}
                        alt="Preview"
                        className="w-full max-h-80 object-contain rounded-lg mx-auto"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPhoto(null);
                          setSelectedFile(null);
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-background/80 rounded-full hover:bg-background transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-4 py-8">
                      <div className="p-4 rounded-2xl bg-primary/10">
                        <Camera className="h-10 w-10 text-primary" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-foreground">Tap to choose a photo</p>
                        <p className="text-xs text-muted-foreground mt-1">JPG, PNG up to 10MB</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />

              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-foreground">For best results:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-primary" />
                    Clear face, looking at the camera
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-primary" />
                    Good lighting (natural light works best)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-primary" />
                    One person preferred
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={resetFlow}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 gap-2"
                  disabled={!selectedPhoto}
                  onClick={() => setScreen('style')}
                >
                  <ArrowRight className="h-4 w-4" />
                  Next: Choose Style
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── SCREEN 2.5: STYLE SELECTION ── */}
          {screen === 'style' && (
            <motion.div
              key="style"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-5"
            >
              <button
                onClick={() => setScreen('upload')}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Photo
              </button>

              <div className="text-center space-y-1">
                <h2 className="text-lg font-bold text-foreground flex items-center justify-center gap-2">
                  <Palette className="h-5 w-5 text-primary" />
                  Customize Your Look
                </h2>
                <p className="text-xs text-muted-foreground">Pick a style, set the vibe, and make it yours.</p>
              </div>

              {/* Photo preview thumbnail */}
              {selectedPhoto && (
                <div className="flex justify-center">
                  <img
                    src={selectedPhoto}
                    alt="Your photo"
                    className="h-24 w-24 rounded-xl object-cover border-2 border-primary/20"
                  />
                </div>
              )}

              <AffirmationStyleControls settings={styleSettings} onChange={setStyleSettings} />

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setScreen('upload')}>
                  Back
                </Button>
                <Button
                  className="flex-1 gap-2"
                  onClick={handleGenerate}
                >
                  <Sparkles className="h-4 w-4" />
                  Create My Affirmation
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── SCREEN 3: GENERATING ── */}
          {screen === 'generating' && (
            <motion.div
              key="generating"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex flex-col items-center justify-center min-h-[60vh] space-y-8"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                className="p-5 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20"
              >
                <Sparkles className="h-10 w-10 text-primary" />
              </motion.div>

              <div className="text-center space-y-2">
                <h2 className="text-lg font-bold text-foreground">
                  Creating your personalized affirmation...
                </h2>
                <p className="text-sm text-muted-foreground">
                  This may take a moment.
                </p>
              </div>

              <div className="w-full max-w-xs space-y-2">
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-muted-foreground text-center">
                  {Math.round(progress)}%
                </p>
              </div>

              <motion.div
                className="flex gap-2"
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {['💛', '✨', '🌟'].map((emoji, i) => (
                  <span key={i} className="text-2xl">{emoji}</span>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* ── SCREEN 4: RESULT ── */}
          {screen === 'result' && result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <button
                onClick={resetFlow}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <Card className="overflow-hidden">
                <img
                  src={result.imageUrl}
                  alt={result.affirmationText}
                  className="w-full aspect-[4/5] object-cover"
                />
              </Card>

              {/* Post-generation emotional nudge */}
              <div className="text-center space-y-1.5 px-4">
                <h3 className="text-lg font-bold text-foreground">This is how you support yourself.</h3>
                <p className="text-sm text-muted-foreground italic">
                  Now imagine doing this for someone else.
                </p>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button className="gap-2" onClick={handleShare}>
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                  <Button variant="outline" className="gap-2" onClick={handleDownload}>
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>

                <Button
                  variant="secondary"
                  className="w-full gap-2"
                  onClick={() => navigate('/create')}
                >
                  <Heart className="h-4 w-4" />
                  Send a message to someone who matters
                </Button>

                {/* Post-generation replay CTAs */}
                {remaining > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      className="gap-1.5 text-xs"
                      onClick={tryAnotherStyle}
                    >
                      <Palette className="h-3.5 w-3.5" />
                      Try Another Style
                    </Button>
                    <Button
                      variant="outline"
                      className="gap-1.5 text-xs"
                      onClick={tryAnotherStyle}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Remix This
                    </Button>
                  </div>
                )}

                {remaining > 0 && (
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={startNew}
                  >
                    <Upload className="h-4 w-4" />
                    Create Another
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {/* ── SCREEN 5: LIMIT ── */}
          {screen === 'limit' && (
            <motion.div
              key="limit"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 text-center"
            >
              <div className="p-4 rounded-2xl bg-muted">
                <Lock className="h-10 w-10 text-muted-foreground" />
              </div>

              {planTier === 'free' ? (
                <div className="space-y-2">
                  <h2 className="text-xl font-bold text-foreground">
                    You've used your free affirmation
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Upgrade to create more personalized affirmation characters and keep sharing what matters.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <h2 className="text-xl font-bold text-foreground">
                    You've reached your monthly limit
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Your affirmations will reset next month, or upgrade to a higher plan to create more now.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3 w-full max-w-xs">
                <Button className="w-full gap-2" onClick={() => navigate('/add-credits')}>
                  <Crown className="h-4 w-4" />
                  {planTier === 'free' ? 'Upgrade Plan' : 'View Plans'}
                </Button>
                <Button variant="ghost" className="w-full" onClick={resetFlow}>
                  Maybe Later
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this affirmation?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the character from your gallery. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
